import {
  DndContext, DragOverlay, KeyboardSensor, PointerSensor,
  pointerWithin, rectIntersection,
  useSensor, useSensors,
  type CollisionDetection, type DragEndEvent, type DragOverEvent, type DragStartEvent,
} from '@dnd-kit/core';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'reactstrap';
import { PoolColumn } from './PoolColumn';
import { OperatorColumn, opCardId } from './OperatorColumn';
import { AssignmentCard } from './AssignmentCard';
import type { AssignmentBoard as Board, AssignedMission } from '../../api/assignments';

export interface AssignmentBoardCallbacks {
  // beforeMissionId (SP-UX): when the list/shard is dropped ON a card, assign it
  // and place it right BEFORE that mission (= exact drop position → claim_order).
  // onAssignWhole resolves with the NEW mission id (to flash the landed card).
  onAssignWhole: (vars: { pickListId: number; operatorUserId: number; beforeMissionId?: number }) => void | Promise<number | void>;
  onAssignOperator: (vars: { missionId: number; operatorUserId: number; beforeMissionId?: number }) => void;
  onCancelWhole: (vars: { missionId: number }) => void;
  onUnassignOperator: (vars: { missionId: number }) => void;
  onReorder: (vars: { operatorUserId: number; missionIds: number[] }) => void;
  onOpenSplitWizard: (pickListId: number) => void;
  // Riporta una wizard_shard nel pool (operator=NULL, status=pending): NON la
  // cancella. Endpoint /admin/missions/{id}/unassign (source-agnostico).
  onUnassignMission: (vars: { missionId: number }) => void;
}

export interface DragSource {
  cardId: string;
  origin: 'pool-pl' | 'pool-shard' | 'op';
  pickListId?: number;
  missionId?: number;
  fromOperatorUserId?: number;
  cardKind?: AssignedMission['kind'];
}

// Persistenza dello stato di collasso delle card operatore. Chiave unica
// globale: gli operatori sono single-membership (un solo magazzino), quindi i
// loro user_id sono disgiunti tra magazzini → nessun "bleed" cross-warehouse.
const COLLAPSED_STORAGE_KEY = 'vp-assign-collapsed-ops';

function loadCollapsedOps(): Set<number> {
  try {
    const raw = localStorage.getItem(COLLAPSED_STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? new Set(parsed.filter((n): n is number => typeof n === 'number')) : new Set();
  } catch {
    return new Set();
  }
}

export function parseDragId(cardId: string, board: Board): DragSource | null {
  if (cardId.startsWith('pool-pl-')) {
    return { cardId, origin: 'pool-pl', pickListId: Number(cardId.slice('pool-pl-'.length)) };
  }
  if (cardId.startsWith('pool-shard-')) {
    return { cardId, origin: 'pool-shard', missionId: Number(cardId.slice('pool-shard-'.length)) };
  }
  if (cardId.startsWith('op-mission-')) {
    const missionId = Number(cardId.slice('op-mission-'.length));
    for (const a of board.assignments) {
      const m = a.missions.find((x) => x.mission_id === missionId);
      if (m) return {
        cardId, origin: 'op', missionId,
        fromOperatorUserId: a.operator_user_id, cardKind: m.kind,
      };
    }
  }
  return null;
}

export type DropAction =
  | { type: 'reorder'; operatorUserId: number; missionIds: number[] }
  | { type: 'assignWhole'; pickListId: number; operatorUserId: number; beforeMissionId?: number }
  | { type: 'assignOperator'; missionId: number; operatorUserId: number; beforeMissionId?: number }
  | { type: 'cancelWhole'; missionId: number }
  | { type: 'unassignOperator'; missionId: number }
  | { type: 'unassignMission'; missionId: number };

/**
 * Pure resolution of a drop into the action to dispatch. Extracted from the
 * DnD handler so it can be unit-tested without simulating pointer events.
 *
 * - drop on a card of the SAME operator (source is an op card) → reorder.
 * - drop on a card of ANOTHER operator / from the pool → assign, placed BEFORE
 *   that card (exact drop position → priority).
 * - drop on an operator column (empty area) → assign, appended to the queue.
 * - drop on the pool → cancel/unassign (depending on card kind).
 */
export function resolveDrop(src: DragSource, overId: string, board: Board): DropAction | null {
  // Active/done op cards can't be moved (their listeners are disabled, but be
  // defensive here too rather than issue a mutation the backend rejects).
  if (src.origin === 'op' && src.missionId) {
    const mission = board.assignments.flatMap((a) => a.missions).find((m) => m.mission_id === src.missionId);
    if (mission && mission.status !== 'pending' && mission.status !== 'assigned') return null;
  }

  // Drop ON a card → assign-at-position or reorder.
  if (overId.startsWith('op-mission-')) {
    const overMissionId = Number(overId.slice('op-mission-'.length));
    let target: { opId: number; idx: number; missions: AssignedMission[] } | null = null;
    for (const a of board.assignments) {
      const idx = a.missions.findIndex((m) => m.mission_id === overMissionId);
      if (idx >= 0) { target = { opId: a.operator_user_id, idx, missions: a.missions }; break; }
    }
    if (!target) return null;

    // Same operator + source is an op card → reorder within the column.
    if (src.origin === 'op' && src.fromOperatorUserId === target.opId) {
      const fromIdx = target.missions.findIndex((m) => m.mission_id === src.missionId);
      if (fromIdx === -1 || fromIdx === target.idx) return null;
      const reordered = [...target.missions];
      const [moved] = reordered.splice(fromIdx, 1);
      reordered.splice(target.idx, 0, moved);
      // Only pending/assigned missions carry a claim_order and are what the
      // backend reorder endpoint manages (it set-equality-checks the payload
      // against the operator's pending+assigned set). An operator column also
      // renders the in-progress (active) / done missions — they must NOT be in
      // the payload, otherwise the set mismatches and the reorder is rejected,
      // making the queued (assigned) missions impossible to reorder.
      const missionIds = reordered
        .filter((m) => m.status === 'pending' || m.status === 'assigned')
        .map((m) => m.mission_id);
      return { type: 'reorder', operatorUserId: target.opId, missionIds };
    }
    // Otherwise assign to the target operator at the dropped slot.
    if (src.origin === 'pool-pl') {
      return { type: 'assignWhole', pickListId: src.pickListId!, operatorUserId: target.opId, beforeMissionId: overMissionId };
    }
    if (src.origin === 'pool-shard' || src.origin === 'op') {
      return { type: 'assignOperator', missionId: src.missionId!, operatorUserId: target.opId, beforeMissionId: overMissionId };
    }
    return null;
  }

  // Drop on the pool column.
  if (overId === 'pool') {
    if (src.origin === 'op' && src.cardKind === 'whole_list') return { type: 'cancelWhole', missionId: src.missionId! };
    if (src.origin === 'op' && src.cardKind === 'self_claim_shard') return { type: 'unassignOperator', missionId: src.missionId! };
    // wizard_shard → unassign (torna nel pool), NON cancel. Un tempo cancellava
    // la missione (status=cancelled) e spariva definitivamente: era il bug.
    if (src.origin === 'op' && src.cardKind === 'wizard_shard') return { type: 'unassignMission', missionId: src.missionId! };
    return null;
  }

  // Drop on an operator column (empty area) → assign, appended.
  if (overId.startsWith('op-')) {
    const targetOpId = Number(overId.slice('op-'.length));
    if (src.origin === 'pool-pl') return { type: 'assignWhole', pickListId: src.pickListId!, operatorUserId: targetOpId };
    if (src.origin === 'pool-shard' || src.origin === 'op') return { type: 'assignOperator', missionId: src.missionId!, operatorUserId: targetOpId };
  }
  return null;
}

/**
 * Compute the ordered mission-id payload that places `newMissionId` right BEFORE
 * `beforeMissionId` in the target operator's queue. Returns the list to hand to
 * the reorder endpoint, or null when no reorder is possible/needed.
 *
 * Only pending/assigned missions carry a claim_order and are what the backend
 * reorder endpoint manages: it set-equality-checks the payload against the
 * operator's pending+assigned set. Including active / awaiting_close / done
 * missions (which the board payload also carries) makes the set mismatch, the
 * reorder is rejected, and the just-assigned mission stays appended at the
 * bottom — the "si accoda in fondo" bug. So we filter to pending/assigned here,
 * exactly as resolveDrop does for the within-column reorder path.
 */
export function computePlacement(
  board: Board,
  operatorUserId: number,
  newMissionId: number | undefined,
  beforeMissionId: number | undefined,
): number[] | null {
  if (newMissionId == null || beforeMissionId == null) return null;
  const op = board.assignments.find((a) => a.operator_user_id === operatorUserId);
  if (!op) return null;
  const ids = op.missions
    .filter((m) => m.status === 'pending' || m.status === 'assigned')
    .map((m) => m.mission_id)
    .filter((id) => id !== newMissionId);
  const idx = ids.indexOf(beforeMissionId);
  if (idx < 0) return null;
  ids.splice(idx, 0, newMissionId);
  return ids;
}

/**
 * Where a drop would land, expressed as {operator, insertion index} in that
 * operator's RENDERED (non-done) mission list. Drives the live "gap opens" hint
 * while dragging over an operator. Returns null when no manual hint is needed:
 * over the pool, an unknown target, or a within-column reorder (dnd-kit's native
 * sortable already shifts the siblings for that case).
 */
export interface PreviewTarget { opId: number; index: number; }

export function previewTargetFromOver(
  board: Board,
  overId: string,
  activeSourceOpId: number | null,
): PreviewTarget | null {
  const rendered = (a: Board['assignments'][number]) => a.missions.filter((m) => m.status !== 'done');
  if (overId.startsWith('op-mission-')) {
    const mid = Number(overId.slice('op-mission-'.length));
    for (const a of board.assignments) {
      const idx = rendered(a).findIndex((m) => m.mission_id === mid);
      if (idx >= 0) {
        if (activeSourceOpId === a.operator_user_id) return null; // native sortable
        return { opId: a.operator_user_id, index: idx };
      }
    }
    return null;
  }
  if (overId.startsWith('op-')) {
    const opId = Number(overId.slice('op-'.length));
    if (activeSourceOpId === opId) return null;
    const a = board.assignments.find((x) => x.operator_user_id === opId);
    if (!a) return null;
    return { opId, index: rendered(a).length }; // append
  }
  return null;
}

/** Rect subset needed for vertical hit-testing a card (viewport coords). */
export interface CardRect { top: number; height: number; }

/**
 * When the pointer is inside an operator column but NOT directly over a queued
 * card (over the header, the gaps, the in-progress card, or the empty body),
 * decide which queued card it should insert BEFORE, from the pointer's vertical
 * position. This is what makes a drop land at the pointer instead of appending —
 * the whole point of "inseriscila dove la rilascio". Returns the target
 * `op-mission-<id>` card id, or null to append (pointer below all queued cards,
 * or the operator has no queue).
 *
 * Only pending/assigned cards are candidates: active / awaiting_close / done
 * carry no claim_order and are skipped, so you can never try to jump ahead of
 * the task the operator is currently picking.
 */
export function insertionTargetInColumn(
  board: Board,
  operatorUserId: number,
  pointerY: number,
  rectOf: (cardId: string) => CardRect | undefined,
  activeCardId: string,
): string | null {
  const op = board.assignments.find((a) => a.operator_user_id === operatorUserId);
  if (!op) return null;
  const queued = op.missions.filter((m) => m.status === 'pending' || m.status === 'assigned');
  for (const m of queued) {
    const cardId = opCardId(m.mission_id);
    if (cardId === activeCardId) continue;
    const rect = rectOf(cardId);
    if (rect && pointerY < rect.top + rect.height / 2) return cardId;
  }
  return null;
}

export function AssignmentBoard({ board, callbacks }: { board: Board; callbacks: AssignmentBoardCallbacks }) {
  const { t } = useTranslation('assignment');
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor),
  );
  const [activeId, setActiveId] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Live "gap opens" hint while dragging over an operator: the target operator +
  // the insertion index, plus the dragged card's height (so the gap matches).
  // Cross-column only — within-column reorder relies on dnd-kit's native shift.
  const [preview, setPreview] = useState<PreviewTarget | null>(null);
  const [previewShiftPx, setPreviewShiftPx] = useState(96);

  // Card operatore collassate (per user_id). Vista compatta "chi lavora ora":
  // header completo + solo missioni in corso. Toggle globale + per-operatore.
  // Persistito in localStorage (sopravvive al reload).
  const [collapsedOps, setCollapsedOps] = useState<Set<number>>(loadCollapsedOps);
  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSED_STORAGE_KEY, JSON.stringify([...collapsedOps]));
    } catch {
      /* quota/private-mode: la persistenza è best-effort, non bloccante. */
    }
  }, [collapsedOps]);
  const allCollapsed = board.assignments.length > 0
    && board.assignments.every((a) => collapsedOps.has(a.operator_user_id));
  function toggleAllCollapse(): void {
    setCollapsedOps(allCollapsed ? new Set() : new Set(board.assignments.map((a) => a.operator_user_id)));
  }
  function toggleOpCollapse(id: number): void {
    setCollapsedOps((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  // SP-UX — flash the just-moved card green for ~3s so it's clear which one
  // moved (survives the board refetch since it's local state keyed by mission).
  const [highlightId, setHighlightId] = useState<number | null>(null);
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  function flashMission(missionId: number | undefined): void {
    if (typeof missionId !== 'number') return;
    setHighlightId(missionId);
    if (flashTimer.current) clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setHighlightId(null), 3000);
  }
  useEffect(() => () => { if (flashTimer.current) clearTimeout(flashTimer.current); }, []);

  // Pointer-based collision: the drop target is whatever is UNDER the cursor.
  // Precedence:
  //  1) directly over a queued (pending/assigned) card → that card wins over its
  //     column (precise reorder / insert exactly where you point);
  //  2) elsewhere inside an operator column (header, gaps, the in-progress card,
  //     empty body) → resolve to the queued card the pointer sits above, so a
  //     drop from the pool / another operator lands at that position instead of
  //     appending. Below all queued cards → keep the column (append).
  // This makes cross-column drops behave like within-column reordering, fixing
  // "si accoda in fondo" when you don't land pixel-perfect on a card.
  const collisionDetection: CollisionDetection = (args) => {
    const pointer = pointerWithin(args);
    const base = pointer.length > 0 ? pointer : rectIntersection(args);
    const activeId = String(args.active.id);

    const queuedCardIds = new Set(
      board.assignments.flatMap((a) => a.missions
        .filter((m) => m.status === 'pending' || m.status === 'assigned')
        .map((m) => opCardId(m.mission_id))),
    );

    const overCard = base.find((c) => queuedCardIds.has(String(c.id)) && String(c.id) !== activeId);
    if (overCard) return [overCard];

    const overCol = base.find((c) => {
      const id = String(c.id);
      return id.startsWith('op-') && !id.startsWith('op-mission-');
    });
    const py = args.pointerCoordinates?.y;
    if (overCol && py != null) {
      const opId = Number(String(overCol.id).slice('op-'.length));
      const target = insertionTargetInColumn(board, opId, py, (id) => args.droppableRects.get(id), activeId);
      if (target) return [{ id: target }];
    }
    return base;
  };

  function handleDragStart(e: DragStartEvent): void {
    setActiveId(String(e.active.id));
    // Size the preview gap to the dragged card so it matches what will land.
    const h = e.active.rect.current.initial?.height;
    if (typeof h === 'number' && h > 0) setPreviewShiftPx(h);
  }

  function handleDragOver(e: DragOverEvent): void {
    if (!e.over) { setPreview(null); return; }
    const src = parseDragId(String(e.active.id), board);
    const sourceOpId = src?.origin === 'op' ? src.fromOperatorUserId ?? null : null;
    const target = previewTargetFromOver(board, String(e.over.id), sourceOpId);
    // Don't show a manual gap on a collapsed operator (its queue is hidden).
    setPreview(target && collapsedOps.has(target.opId) ? null : target);
  }

  function handleDragEnd(e: DragEndEvent): void {
    setActiveId(null);
    setPreview(null);
    if (!e.over) return;
    const src = parseDragId(String(e.active.id), board);
    if (!src) return;
    const action = resolveDrop(src, String(e.over.id), board);
    if (!action) return;
    switch (action.type) {
      case 'reorder':
        callbacks.onReorder({ operatorUserId: action.operatorUserId, missionIds: action.missionIds });
        flashMission(src.missionId); // the moved op card keeps its mission id
        break;
      case 'assignWhole': {
        // The mission is created server-side → flash the id it resolves with.
        const res = callbacks.onAssignWhole({ pickListId: action.pickListId, operatorUserId: action.operatorUserId, beforeMissionId: action.beforeMissionId });
        Promise.resolve(res).then((newId) => flashMission(typeof newId === 'number' ? newId : undefined));
        break;
      }
      case 'assignOperator':
        callbacks.onAssignOperator({ missionId: action.missionId, operatorUserId: action.operatorUserId, beforeMissionId: action.beforeMissionId });
        flashMission(action.missionId); // existing mission keeps its id when reassigned
        break;
      case 'cancelWhole':
        callbacks.onCancelWhole({ missionId: action.missionId });
        break;
      case 'unassignOperator':
        callbacks.onUnassignOperator({ missionId: action.missionId });
        break;
      case 'unassignMission':
        callbacks.onUnassignMission({ missionId: action.missionId });
        break;
    }
  }

  function handleCardClick(cardId: string, e: React.MouseEvent): void {
    e.stopPropagation();
    if (e.ctrlKey || e.metaKey) {
      const next = new Set(selected);
      next.has(cardId) ? next.delete(cardId) : next.add(cardId);
      setSelected(next);
    } else if (e.shiftKey) {
      setSelected(new Set([...selected, cardId]));
    } else {
      setSelected(new Set([cardId]));
    }
  }

  function handleBackgroundClick(e: React.MouseEvent): void {
    if (e.target === e.currentTarget && selected.size > 0) setSelected(new Set());
  }

  const operatorById = Object.fromEntries(board.operators.map((o) => [o.user_id, o]));

  // Visual proxy that follows the cursor — the REAL card, so it's clearly
  // visible and (being in the DragOverlay) always on top of the columns.
  const noop = () => {};
  function overlayContent(): React.ReactNode {
    if (!activeId) return null;
    if (selected.size > 1 && selected.has(activeId)) {
      return (
        <div style={{ padding: 6, background: 'var(--vp-primary)', color: '#fff',
                      borderRadius: 'var(--vp-r-xs)', boxShadow: 'var(--vp-shadow-md)' }}>
          {t('selection.elementsOverlay_other', { count: selected.size })}
        </div>
      );
    }
    if (activeId.startsWith('pool-pl-')) {
      const plId = Number(activeId.slice('pool-pl-'.length));
      const card = board.pool.find((c) => c.kind === 'whole_list' && c.pick_list.id === plId);
      if (card && card.kind === 'whole_list') {
        return <AssignmentCard kind="whole_list" location="pool" pickList={card.pick_list} onClick={noop} />;
      }
    }
    if (activeId.startsWith('pool-shard-')) {
      const mid = Number(activeId.slice('pool-shard-'.length));
      const card = board.pool.find((c) => c.kind === 'self_claim_shard' && c.mission_id === mid);
      if (card && card.kind === 'self_claim_shard') {
        return <AssignmentCard kind="self_claim_shard" location="pool" pickList={card.pick_list}
                 shardIndex={card.shard_index} shardTotal={card.shard_total}
                 qtyAssignedTotal={card.qty_assigned_total} taskCount={card.task_count} onClick={noop} />;
      }
    }
    if (activeId.startsWith('op-mission-')) {
      const mid = Number(activeId.slice('op-mission-'.length));
      for (const a of board.assignments) {
        const m = a.missions.find((x) => x.mission_id === mid);
        if (m) {
          return <AssignmentCard kind={m.kind} location="op" pickList={m.pick_list} claimOrder={m.claim_order}
                   shardIndex={m.kind === 'whole_list' ? undefined : m.shard_index}
                   shardTotal={m.kind === 'whole_list' ? undefined : m.shard_total}
                   qtyAssignedTotal={m.qty_assigned_total} taskCount={m.task_count} status={m.status}
                   onClick={noop} />;
        }
      }
    }
    return null;
  }

  return (
    <DndContext sensors={sensors} collisionDetection={collisionDetection}
                onDragStart={handleDragStart} onDragOver={handleDragOver}
                onDragEnd={handleDragEnd} onDragCancel={() => { setActiveId(null); setPreview(null); }}>
      {selected.size > 0 && (
        <div role="status" aria-label={t('selection.multipleAria')}
             style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <span style={{ fontSize: 'var(--vp-text-sm)' }}>
            {selected.size === 1 ? t('selection.selected_one') : t('selection.selected_other', { count: selected.size })}
          </span>
          <Button type="button" color="light" size="sm"
                  onClick={() => setSelected(new Set())}>{t('selection.deselect')}</Button>
        </div>
      )}
      <div onClick={handleBackgroundClick} className="vp-assignment-board">
        {/* Colonna "Da assegnare": si restringe quando è vuota (resta droppable). */}
        <aside className={`vp-board-pool ${board.pool.length === 0 ? 'vp-board-pool--empty' : ''}`}>
          <PoolColumn cards={board.pool} onCardClick={handleCardClick}
                      selectedIds={selected} onSplit={callbacks.onOpenSplitWizard} />
        </aside>

        {/* Griglia responsive degli operatori */}
        <section className="vp-board-ops">
          <div className="vp-board-ops__head">
            <span className="fw-semibold d-inline-flex align-items-center gap-2">
              <i className="bi bi-people text-muted" /> {t('operators.header')}
              <span className="badge rounded-pill bg-light text-muted fw-medium">{board.assignments.length}</span>
            </span>
            <div className="d-flex align-items-center gap-3">
              <span className="vp-board-legend">
                <span><span className="vp-dot vp-dot--active" /> {t('operators.legendActive')}</span>
                <span><span className="vp-dot vp-dot--free" /> {t('operators.legendFree')}</span>
              </span>
              {board.assignments.length > 0 && (
                <Button type="button" color="light" size="sm"
                        className="d-inline-flex align-items-center gap-1"
                        onClick={toggleAllCollapse}
                        aria-pressed={allCollapsed}>
                  <i className={`bi bi-chevron-${allCollapsed ? 'down' : 'up'}`} />
                  {allCollapsed ? t('operators.expandAll') : t('operators.collapseAll')}
                </Button>
              )}
            </div>
          </div>
          <div className="vp-board-ops__grid">
            {board.assignments.map((a, idx) => (
              <OperatorColumn key={a.operator_user_id} operatorUserId={a.operator_user_id}
                              colorIndex={idx}
                              label={operatorById[a.operator_user_id]?.label ?? `#${a.operator_user_id}`}
                              // Le missioni completate non vanno mostrate sulla board:
                              // restano nel `board` (per la logica drag/resolveDrop), ma
                              // non vengono renderizzate nella colonna dell'operatore.
                              missions={a.missions.filter((m) => m.status !== 'done')}
                              highlightMissionId={highlightId}
                              collapsed={collapsedOps.has(a.operator_user_id)}
                              onToggleCollapse={() => toggleOpCollapse(a.operator_user_id)}
                              previewIndex={preview?.opId === a.operator_user_id ? preview.index : null}
                              previewShiftPx={previewShiftPx}
                              onCardClick={handleCardClick} selectedIds={selected} />
            ))}
          </div>
        </section>
      </div>
      <DragOverlay dropAnimation={null} zIndex={1100}>
        {activeId ? (
          <div style={{ cursor: 'grabbing', borderRadius: 'var(--vp-r-xs)',
                        boxShadow: '0 0 0 3px rgba(61,163,0,.95), 0 12px 28px rgba(0,0,0,.28)', rotate: '1.5deg' }}>
            {overlayContent()}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
